import React, { useState, useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { analyticsService, AnalyticsEvent, ConversionEvent } from '../utils/analytics';
import { useBooking } from './BookingProvider';
import { primaryDemoCta } from '../content/siteMessaging';

const appUrl = import.meta.env.VITE_APP_URL || 'https://app.cplng.org';

const Header: React.FC = () => {
    const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
    const [scrolled, setScrolled] = useState(false);

    const location = useLocation();
    const navigate = useNavigate();
    const booking = useBooking();

    const isHome = location.pathname === '/';

    const handleBookDemoClick = () => {
        analyticsService.logEvent(AnalyticsEvent.CTA_CLICK, {
            cta_name: 'book_demo',
            location: 'header',
        });
        setMobileMenuOpen(false);
        booking.open('demo');
    };

    useEffect(() => {
        const handleScroll = () => {
            setScrolled(window.scrollY > 50);
        };
        window.addEventListener('scroll', handleScroll);
        return () => window.removeEventListener('scroll', handleScroll);
    }, []);

    const scrollToSection = (sectionId: string) => {
        setMobileMenuOpen(false);

        if (!isHome) {
            navigate(`/#${sectionId}`);
            window.location.href = `/#${sectionId}`;
        } else {
            const element = document.getElementById(sectionId);
            if (element) {
                element.scrollIntoView({ behavior: 'smooth' });
            }
        }
    };

    const handleLogoClick = (e: React.MouseEvent) => {
        e.preventDefault();
        if (isHome) {
            window.scrollTo({ top: 0, behavior: 'smooth' });
        } else {
            navigate('/');
        }
        setMobileMenuOpen(false);
    };

    const handleLoginClick = () => {
        analyticsService.logEvent(AnalyticsEvent.CTA_CLICK, { cta_name: 'login' });
        window.location.href = `${appUrl}/login?source=coupling-wnc`;
    };

    const handleBlogClick = () => {
        navigate('/blog');
        setMobileMenuOpen(false);
    }

    // Dynamic color classes based on scroll state
    const logoSrc = scrolled
        ? '/logo/coupling-logo-light.svg'
        : '/logo/coupling-logo-mono.svg';
    const logoColor = scrolled ? 'text-neutral-900' : 'text-white';
    const navLinkColor = scrolled
        ? 'text-neutral-600 hover:text-neutral-900'
        : 'text-white/80 hover:text-white';
    const hamburgerColor = scrolled
        ? 'text-neutral-600 hover:text-neutral-900'
        : 'text-white/80 hover:text-white';
    const loginBtnClass = scrolled
        ? 'text-white bg-brand-primary hover:bg-brand-primary-dark shadow-lg hover:shadow-brand-primary/25'
        : 'text-white bg-white/20 hover:bg-white/30 backdrop-blur-sm';
    // Book-a-demo uses the inverse style so it pops alongside Login without clashing:
    // outlined when scrolled (vs Login's filled), and a soft white fill when transparent.
    const bookBtnClass = scrolled
        ? 'text-brand-primary border-2 border-brand-primary hover:bg-brand-primary/10'
        : 'text-white border-2 border-white/60 hover:bg-white/10 backdrop-blur-sm';

    return (
        <header
            className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${scrolled
                ? 'bg-white/95 backdrop-blur-md border-b border-neutral-200 shadow-sm'
                : 'bg-transparent'
                }`}
        >
            <nav className="container mx-auto px-4">
                <div className="flex justify-between items-center h-16 sm:h-20">
                    {/* Logo */}
                    <div className="flex-shrink-0">
                        <a
                            href="/"
                            onClick={handleLogoClick}
                            className="flex items-center space-x-3 hover:opacity-80 transition-opacity"
                        >
                            <img
                                src={logoSrc}
                                alt="Coupling WNC Logo"
                                className="h-8 w-8 sm:h-10 sm:w-10"
                            />
                            <div className="flex flex-col">
                                <span className={`text-xl sm:text-2xl font-bold transition-colors duration-300 leading-tight ${logoColor}`}>
                                    Coupling WNC
                                </span>
                                <span className={`text-xs transition-colors duration-300 ${scrolled ? 'text-neutral-500' : 'text-white/60'}`}>
                                    Part of the Coupling network
                                </span>
                            </div>
                        </a>
                    </div>

                    {/* Desktop Navigation */}
                    <div className="hidden md:flex items-center space-x-8">
                        <button
                            onClick={() => scrollToSection('product-modes')}
                            className={`transition-colors text-sm font-medium ${navLinkColor}`}
                        >
                            Product
                        </button>
                        <button
                            onClick={() => scrollToSection('pos-workflow')}
                            className={`transition-colors text-sm font-medium ${navLinkColor}`}
                        >
                            POS workflow
                        </button>
                        <button
                            onClick={() => scrollToSection('pricing')}
                            className={`transition-colors text-sm font-medium ${navLinkColor}`}
                        >
                            Pricing
                        </button>
                        <a
                            href="/hardware"
                            className={`transition-colors text-sm font-medium ${navLinkColor}`}
                        >
                            Hardware
                        </a>
                        <button
                            onClick={handleBlogClick}
                            className={`transition-colors text-sm font-medium ${navLinkColor}`}
                        >
                            Blog
                        </button>
                        <a
                            href="/app"
                            className={`transition-colors text-sm font-medium ${navLinkColor}`}
                            onClick={() => {
                                analyticsService.logEvent(AnalyticsEvent.CTA_CLICK, { cta_name: 'get_the_app' });
                                // Fire Meta CompleteRegistration on the merchant-acquisition CTA.
                                // NOTE: this fires on intent (click), not actual signup completion.
                                // When app.cplng.org's signup flow can emit a server-side event,
                                // move this to fire from there for higher fidelity.
                                analyticsService.logConversion(ConversionEvent.COMPLETE_REGISTRATION, {
                                    content_name: 'merchant_signup_started',
                                    location: 'header',
                                });
                            }}
                        >
                            Get the App
                        </a>
                        <button
                            onClick={handleBookDemoClick}
                            className={`inline-flex items-center px-5 py-2.5 text-sm font-medium rounded-full transition-all hover:scale-105 ${bookBtnClass}`}
                        >
                            {primaryDemoCta}
                        </button>
                        <button
                            onClick={handleLoginClick}
                            className={`inline-flex items-center px-5 py-2.5 text-sm font-medium rounded-full transition-all hover:scale-105 ${loginBtnClass}`}
                        >
                            Login
                        </button>
                    </div>

                    {/* Mobile Menu Button */}
                    <div className="flex md:hidden items-center space-x-3">
                        <button
                            onClick={handleLoginClick}
                            className={`inline-flex items-center px-4 py-2 text-sm font-medium rounded-full transition-colors ${loginBtnClass}`}
                        >
                            Login
                        </button>
                        <button
                            onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
                            className={`p-2 transition-colors ${hamburgerColor}`}
                            aria-label="Toggle menu"
                        >
                            {mobileMenuOpen ? (
                                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                                </svg>
                            ) : (
                                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
                                </svg>
                            )}
                        </button>
                    </div>
                </div>
            </nav>

            {/* Mobile Menu */}
            {mobileMenuOpen && (
                <div className="md:hidden bg-white/95 backdrop-blur-md border-t border-neutral-200">
                    <div className="px-4 py-4 space-y-2">
                        <button
                            onClick={() => scrollToSection('product-modes')}
                            className="block w-full text-left px-4 py-3 text-base font-medium text-neutral-600 hover:text-neutral-900 hover:bg-neutral-50 rounded-lg transition-colors"
                        >
                            Product
                        </button>
                        <button
                            onClick={() => scrollToSection('pos-workflow')}
                            className="block w-full text-left px-4 py-3 text-base font-medium text-neutral-600 hover:text-neutral-900 hover:bg-neutral-50 rounded-lg transition-colors"
                        >
                            POS workflow
                        </button>
                        <button
                            onClick={() => scrollToSection('pricing')}
                            className="block w-full text-left px-4 py-3 text-base font-medium text-neutral-600 hover:text-neutral-900 hover:bg-neutral-50 rounded-lg transition-colors"
                        >
                            Pricing
                        </button>
                        <a
                            href="/hardware"
                            className="block w-full text-left px-4 py-3 text-base font-medium text-neutral-600 hover:text-neutral-900 hover:bg-neutral-50 rounded-lg transition-colors"
                        >
                            Hardware
                        </a>
                        <button
                            onClick={handleBlogClick}
                            className="block w-full text-left px-4 py-3 text-base font-medium text-neutral-600 hover:text-neutral-900 hover:bg-neutral-50 rounded-lg transition-colors"
                        >
                            Blog
                        </button>
                        <a
                            href="/app"
                            className="block w-full text-left px-4 py-3 text-base font-medium text-neutral-600 hover:text-neutral-900 hover:bg-neutral-50 rounded-lg transition-colors"
                            onClick={() => {
                                analyticsService.logEvent(AnalyticsEvent.CTA_CLICK, { cta_name: 'get_the_app' })
                                analyticsService.logConversion(ConversionEvent.COMPLETE_REGISTRATION, {
                                    content_name: 'get_the_app_mobile_menu',
                                })
                            }}
                        >
                            Get the App
                        </a>
                        <button
                            onClick={handleBookDemoClick}
                            className="block w-full text-left px-4 py-3 mt-2 text-base font-semibold text-white bg-brand-primary hover:bg-brand-primary-dark rounded-lg transition-colors"
                        >
                            {primaryDemoCta}
                        </button>
                    </div>
                </div>
            )}
        </header>
    );
};

export default Header;
